import { useInterval } from "@mantine/hooks";
import { useState } from "react";

import { useDetectedCuesStore } from "@/sales-tools/detected-cues/stores/DetectedCuesStore";
import { useSessionId } from "@/sdk";

import type { CustomerLocationViewProps } from "./CustomerLocation.types";

import { useTimezone } from "./api/useTimezone";
import { getLocationString } from "./getLocationString";

interface UseCustomerLocationDataResult {
    /** Whether the component should render at all */
    shouldRender: boolean;
    /** Props to pass to the view component */
    viewProps?: CustomerLocationViewProps;
}

export function useCustomerLocationData(): UseCustomerLocationDataResult {
    const sessionId = useSessionId();
    const location = useDetectedCuesStore((state) => state.getLocation(sessionId));

    // Time state - updates every second
    const [time, setTime] = useState(new Date());

    const locationText = location ? getLocationString(location) : "";

    // Fetch timezone based on location - always called, but disabled when no location
    const { data: timezone, isLoading: isTimezoneLoading } = useTimezone(locationText, {
        initialData: location?.timezone ?? undefined,
        enabled: !!location,
    });

    // Update time every second
    useInterval(() => setTime(new Date()), 1000, { autoInvoke: true });

    // Early exit after all hooks
    if (!location) {
        return { shouldRender: false };
    }

    // Format time if timezone is available
    const formattedTime = timezone
        ? time
              .toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "2-digit",
                  timeZone: timezone,
              })
              .toLocaleLowerCase()
        : undefined;

    return {
        shouldRender: true,
        viewProps: {
            locationText,
            formattedTime,
            isTimezoneLoading,
        },
    };
}
